import { type FC, memo, useCallback, useEffect, useMemo } from "react";
import { Spinner } from "@stubhub/broadway-web/icons";
import type { EventListGridItem } from "src/http/types";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import type { Dates } from "src/types/shared/cookies";
import { MlbDateCard } from "src/website-apps/responsive/stubhub-performer/components/calendar/mlb/MlbDateCard";
import { CalendarRenderMode } from "src/website-apps/shared/constants/calendar-render-mode";
import type { GridsReducerState } from "src/website-apps/shared/hooks/useGridsReducer";
import { useLocaleDayjs } from "src/website-apps/shared/hooks/useLocaleDayjs";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { getMonthDateKey } from "src/website-apps/shared/utils/calendar";
import { BaseDateCard } from "./BaseDateCard";
import { CalendarNavigation } from "./CalendarNavigation";
import { PerformerDateCard } from "./residency/PerformerDateCard";
import { calendarBodyOverlay, calendarBodyRecipe, calendarMonth, defaultContainerContentFallback } from "./styles";
import { getCalendarDays, isSameMonthAs } from "./utils";

interface Props {
  /** The month's index amongst all months rendered */
  index: number;
  /** The date representing the month to display in the calendar */
  monthDate: Date;
  /** Events organized by month-year key and day number, containing event data for each day */
  eventsByMonthAndDay: Record<string, Record<number, EventListGridItem[]>>;
  /** The rendering mode that determines which type of date cards to display (e.g., MLB-specific cards) */
  renderMode: CalendarRenderMode | null;
  /** Calendar events state from the grids reducer, including loading states */
  calendarEvents: GridsReducerState["calendarEvents"];
  /** Selected dates from the date selector */
  dates: Maybe<Dates>;
  /** Performer's categoryId */
  categoryId: number;
  /** Handler to open notification modal for a specific event */
  openNotificationHandler: (eventId: number) => void;
  /** Click event handler */
  onEventClick?: (event: EventListGridItem) => void;
  /** Mouse enter event handler */
  onEventMouseEnter?: (event: EventListGridItem) => void;
}

/**
 * Renders a single month view of the calendar with navigation and date cards.
 *
 * This component displays a full month calendar grid with event information for each day.
 * It supports different render modes to display specialized date cards (e.g., MLB-specific cards)
 * and handles loading states during calendar updates.
 */

export const CalendarMonth: FC<Props> = memo(
  ({
    index,
    monthDate,
    eventsByMonthAndDay,
    renderMode,
    calendarEvents,
    dates,
    categoryId,
    openNotificationHandler,
    onEventClick,
    onEventMouseEnter,
  }) => {
    const storePageEvent = useStorePageEvent();
    const today = useStableNow();
    const localeDayjs = useLocaleDayjs();
    const isCurrentMonth = useMemo(() => isSameMonthAs(today, monthDate), [monthDate, today]);
    const calendarDays = useMemo(() => getCalendarDays(monthDate, dates, today), [monthDate, dates, today]);
    const monthDateKey = getMonthDateKey(monthDate);
    const eventsForMonth = useMemo(() => eventsByMonthAndDay[monthDateKey] ?? {}, [eventsByMonthAndDay, monthDateKey]);

    const emptyMonth = useMemo(
      () => !calendarEvents?.monthsWithEvents?.includes(monthDateKey),
      [calendarEvents, monthDateKey],
    );

    /** Maps each day number to its sequential index among days with events (for analytics gridIndex) */
    const eventCardIndexByDay = useMemo(
      () =>
        calendarDays.reduce(
          (acc: Record<number, number>, { day }) => ({
            ...acc,
            ...(day && (eventsForMonth[day]?.length ?? 0) > 0 && { [day]: Object.keys(acc).length }),
          }),
          {},
        ),
      [calendarDays, eventsForMonth],
    );

    useEffect(() => {
      if (index !== 0) {
        return;
      }

      const totalEventsInMonth = Object.values(eventsForMonth).reduce(
        (total, dayEvents) => total + dayEvents.length,
        0,
      );
      storePageEvent(PE_TYPE.LOADED, PE_KEYS.CALENDAR_FIRST_MONTH_EVENT_COUNT, String(totalEventsInMonth));
    }, [index, eventsForMonth, storePageEvent]);

    const handleClick = useCallback(
      (event: EventListGridItem | null) => {
        storePageEvent(PE_TYPE.CLICK, PE_KEYS.CALENDAR_CARD_CLICK, String(event?.eventId));
        if (event !== null) {
          onEventClick?.(event);
        }
      },
      [onEventClick, storePageEvent],
    );

    const handleMouseEnter = useCallback(
      (event: EventListGridItem | null) => {
        storePageEvent(PE_TYPE.HOVER, PE_KEYS.CALENDAR_CARD_HOVER, String(event?.eventId));
        if (event !== null) {
          onEventMouseEnter?.(event);
        }
      },
      [onEventMouseEnter, storePageEvent],
    );

    const isResidencyMode = renderMode === CalendarRenderMode.Residency;
    const gridAriaLabel = isResidencyMode ? localeDayjs(monthDate).format("MMMM YYYY") : undefined;

    return (
      <div className={calendarMonth}>
        {renderMode !== CalendarRenderMode.Residency && <CalendarNavigation monthDate={monthDate} />}
        <div
          className={calendarBodyRecipe({ loading: calendarEvents?.isUpdating ? "loading" : "idle" })}
          {...(isResidencyMode ? { role: "grid", "aria-label": gridAriaLabel } : {})}
        >
          {calendarDays.map(({ key, day }) => {
            if (!day) {
              return isResidencyMode ? (
                <PerformerDateCard
                  key={key}
                  dayOfMonth={null}
                  events={[]}
                  today={false}
                  onClick={() => undefined}
                  onMouseEnter={() => undefined}
                  calendarIndex={-1}
                  openNotificationHandler={() => undefined}
                />
              ) : (
                <BaseDateCard key={key} dayOfMonth={null} />
              );
            }

            const isToday = isCurrentMonth && day === today.date();
            const dayEvents = day ? (eventsForMonth[day] ?? []) : [];
            const [singleEvent] = dayEvents;

            /*
             * Renders MLB or Residency date card based on renderMode. As we add more
             * categories and genres, we should add additional enum values to render the appropriate card type.
             */
            switch (renderMode) {
              case CalendarRenderMode.Residency: {
                return (
                  <PerformerDateCard
                    key={key}
                    events={dayEvents}
                    dayOfMonth={day}
                    today={isToday}
                    onClick={handleClick}
                    onMouseEnter={handleMouseEnter}
                    calendarIndex={eventCardIndexByDay[day] ?? -1}
                    openNotificationHandler={openNotificationHandler}
                  />
                );
              }
              case CalendarRenderMode.MLB: {
                return (
                  <MlbDateCard
                    key={key}
                    events={dayEvents}
                    dayOfMonth={day}
                    categoryId={categoryId}
                    today={isToday}
                    openNotificationHandler={openNotificationHandler}
                    onClick={handleClick}
                    onMouseEnter={handleMouseEnter}
                    calendarIndex={eventCardIndexByDay[day] ?? -1}
                  />
                );
              }
              default: {
                return (
                  <BaseDateCard
                    key={key}
                    eventURL={singleEvent?.url ?? "#"}
                    dayOfMonth={day}
                    today={isToday}
                    onClick={handleClick}
                    onMouseEnter={handleMouseEnter}
                  >
                    {singleEvent?.name && <div className={defaultContainerContentFallback}>{singleEvent.name}</div>}
                  </BaseDateCard>
                );
              }
            }
          })}
        </div>
        {calendarEvents?.isUpdating && !emptyMonth && (
          <div className={calendarBodyOverlay}>
            <Spinner size={40} />
          </div>
        )}
      </div>
    );
  },
);
CalendarMonth.displayName = "CalendarMonth";
